import { useRequiredContext } from "@redotech/react-util/context";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { toast } from "@redotech/redo-web/alert";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { memo, useState } from "react";
import {
  PinVerificationType,
  usePinVerification,
} from "../common/pin-verification-context";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

interface OverrideNotReceivedModalProps {
  isOpen: boolean;
  fulfillmentGroups: readonly FulfillmentGroupData[];
  onClose: () => void;
}

export const OverrideNotReceivedModal = memo(function OverrideNotReceivedModal({
  isOpen,
  fulfillmentGroups,
  onClose,
}: OverrideNotReceivedModalProps) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const { executeWithPinCheck } = usePinVerification();
  const [isLoading, setIsLoading] = useState(false);

  const groupCount = fulfillmentGroups.length;
  const subtitle =
    groupCount === 1
      ? "You are about to override the not received status for this shipment and mark it as open."
      : `You are about to override the not received status for ${groupCount} shipments and mark them as open.`;

  const performOverride = async () => {
    setIsLoading(true);
    try {
      await client.overrideNotReceivedGroups({
        fulfillmentGroupIds: fulfillmentGroups.map((g) => g._id),
      });
      toast(
        `Successfully overrode not received status for ${groupCount} ${groupCount === 1 ? "shipment" : "shipments"}`,
        { variant: "success" },
      );
      onClose();
    } catch (error) {
      toast("Failed to override not received status", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    executeWithPinCheck(
      performOverride,
      { type: PinVerificationType.OverrideNotReceived },
      {
        title: "Override Not Received Status",
        description:
          "Please enter your PIN to override the not received status.",
      },
    );
  };

  return (
    <RedoModal
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Continue",
        onClick: handleConfirm,
        pending: isLoading,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={subtitle}
      title="Override not received status"
    />
  );
});
